import React, { useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";
import Table from "components/Table";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { LCCComponentResult } from "api-client";
import { IconCube, IconStack2 } from "@tabler/icons-react";
import { getCurrentReferenceUnitValue } from "features/MappingEditor/utils";
import Tooltip from "components/Tooltip";
import { lccStore } from "./LCCStore";

const formatUnit = (unit: string | null | undefined): string => {
  if (!unit) return "";
  const normalized = unit.trim().toUpperCase();
  const mappedId = normalized === "STK" ? "PCS" : normalized;
  const refUnit = getCurrentReferenceUnitValue(mappedId);
  return refUnit?.name ?? unit;
};

const formatCurrency = (
  val: number | null | undefined,
  locale: string,
  suffix: string = "€"
): string =>
  `${val?.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${suffix}`;

const typeIcon = (productType: string | null | undefined) => {
  if (productType?.toUpperCase() === "COMPONENT")
    return <IconStack2 size={18} className="text-gray-500 shrink-0" />;
  return <IconCube size={18} className="text-gray-500 shrink-0" />;
};

const LCCDataTable = () => {
  const { t, i18n } = useTranslation();

  const hasEmpty =
    !lccStore.loadingComponents && lccStore.components.length === 0;

  const columns = useMemo((): ColumnDef<unknown, unknown>[] => {
    const colHeader = (labelKey: string, tooltipKey: string) => (
      <div className="flex items-center gap-1">
        <span>{t(labelKey)}</span>
        <Tooltip content={t(tooltipKey)} />
      </div>
    );
    return [
      {
        header: t("lcc.product"),
        accessorKey: "component_name",
        id: "component_name",
        enableColumnFilter: false,
        enableSorting: true,
        size: 250,
        cell: ({ row }) => {
          const item = row.original as LCCComponentResult;
          return (
            <div className="flex items-center gap-2">
              {typeIcon(item.product_type)}
              <span>{item.component_name}</span>
            </div>
          );
        },
      } as ColumnDef<unknown, unknown>,
      {
        header: () => colHeader("lcc.kgCode", "lcc.kgCodeTooltip"),
        accessorKey: "kg_code",
        id: "kg_code",
        enableColumnFilter: false,
        enableSorting: true,
        size: 80,
      } as ColumnDef<unknown, unknown>,
      {
        header: () => colHeader("lcc.kgName", "lcc.kgNameTooltip"),
        accessorKey: "kg_name",
        id: "kg_name",
        enableColumnFilter: false,
        enableSorting: false,
        size: 150,
      } as ColumnDef<unknown, unknown>,
      {
        header: () => colHeader("lcc.amount", "lcc.amountTooltip"),
        accessorKey: "amount",
        id: "amount",
        enableColumnFilter: false,
        enableSorting: false,
        size: 120,
        cell: ({ row }) => {
          const item = row.original as LCCComponentResult;
          return `${item.amount?.toLocaleString(i18n.language, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} ${formatUnit(item.amount_unit)}`;
        },
      } as ColumnDef<unknown, unknown>,
      {
        header: () =>
          colHeader("lcc.installedCost", "lcc.installedCostTooltip"),
        accessorKey: "installed_cost_eur",
        id: "installed_cost_eur",
        enableColumnFilter: false,
        enableSorting: false,
        size: 140,
        cell: ({ row }) =>
          formatCurrency(
            (row.original as LCCComponentResult).installed_cost_eur,
            i18n.language
          ),
      } as ColumnDef<unknown, unknown>,
      {
        header: () =>
          colHeader("lcc.maintenancePV", "lcc.maintenancePVTooltip"),
        accessorKey: "maintenance_pv_eur",
        id: "maintenance_pv_eur",
        enableColumnFilter: false,
        enableSorting: false,
        size: 140,
        cell: ({ row }) =>
          formatCurrency(
            (row.original as LCCComponentResult).maintenance_pv_eur,
            i18n.language
          ),
      } as ColumnDef<unknown, unknown>,
      {
        header: () =>
          colHeader("lcc.replacementPV", "lcc.replacementPVTooltip"),
        accessorKey: "replacement_pv_eur",
        id: "replacement_pv_eur",
        enableColumnFilter: false,
        enableSorting: false,
        size: 140,
        cell: ({ row }) =>
          formatCurrency(
            (row.original as LCCComponentResult).replacement_pv_eur,
            i18n.language
          ),
      } as ColumnDef<unknown, unknown>,
      {
        header: () => colHeader("lcc.totalPV", "lcc.totalPVTooltip"),
        accessorKey: "total_pv_eur",
        id: "total_pv_eur",
        enableColumnFilter: false,
        enableSorting: false,
        size: 140,
        cell: ({ row }) =>
          formatCurrency(
            (row.original as LCCComponentResult).total_pv_eur,
            i18n.language
          ),
      } as ColumnDef<unknown, unknown>,
      {
        header: () =>
          colHeader("lcc.totalPerM2BGF", "lcc.totalPerM2BgfTooltip"),
        accessorKey: "total_eur_per_m2_bgf",
        id: "total_eur_per_m2_bgf",
        enableColumnFilter: false,
        enableSorting: false,
        size: 130,
        cell: ({ row }) =>
          formatCurrency(
            (row.original as LCCComponentResult).total_eur_per_m2_bgf,
            i18n.language,
            "€/m²"
          ),
      } as ColumnDef<unknown, unknown>,
      {
        header: () =>
          colHeader("lcc.serviceLifeYears", "lcc.serviceLifeYearsTooltip"),
        accessorKey: "service_life_years",
        id: "service_life_years",
        enableColumnFilter: false,
        enableSorting: false,
        size: 120,
        cell: ({ row }) => {
          const val = (row.original as LCCComponentResult).service_life_years;
          return val != null ? `${val} ${t("lcc.years")}` : "";
        },
      } as ColumnDef<unknown, unknown>,
      {
        header: () => colHeader("lcc.level", "lcc.levelTooltip"),
        accessorKey: "level",
        id: "level",
        enableColumnFilter: false,
        enableSorting: false,
        size: 100,
      } as ColumnDef<unknown, unknown>,
    ];
  }, [t, i18n.language]);

  const getColumns = useCallback(() => columns, [columns]);

  const fetchData = useCallback(async (start: number, size: number) => {
    const page = Math.floor(start / size);
    await lccStore.fetchComponents(page, size);
  }, []);

  const handleSortChanged = useCallback((sorting: SortingState) => {
    if (sorting.length === 0) return;
    lccStore.setSortBy(sorting[0].id === "kg_code");
  }, []);

  const data = useMemo(
    () => lccStore.components as unknown[],
    [lccStore.components]
  );

  return (
    <div className="bg-white rounded-lg border border-gray-300">
      {hasEmpty ? (
        <div className="p-4 text-sm text-gray-500 text-center">
          <p className="font-medium text-gray-700">{t("lcc.noComponents")}</p>
          <p className="mt-1">{t("lcc.noComponentsDescription")}</p>
        </div>
      ) : (
        <Table
          data={data}
          getColumns={getColumns}
          fetchData={fetchData}
          totalDBRowCount={lccStore.componentsTotal}
          fetchSize={25}
          enableColumnResizing
          dataFetchLoading={lccStore.loadingComponents}
          hiddenColumns={{ level: false }}
          noFooterBar
          borderedStyle
          scrollDivClassName="bg-white"
          onSortChanged={handleSortChanged}
          defaultSort={[{ id: "component_name", desc: false }]}
        />
      )}
    </div>
  );
};

export default observer(LCCDataTable);
